import { useEffect, useMemo, useRef, useState } from 'react';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import {
  BalanceFilterType,
  Charge,
  ClientBalance,
  LedgerEntry,
  PatientDetailsModalMode,
  PatientDetailsTabName,
  Payment,
  PaymentPlan,
} from './PatientDetailsConstant';
import './PatientDetailsPage.scss';
import { useNavigate, useParams } from 'react-router-dom';
import {
  pausePaymentPlan,
  resumePaymentPlan,
  createPaymentPlan,
  deletePaymentPlan,
  updatePaymentPlan,
  getTransactionLedger,
  fetchBalance,
  getPaymentPlansLatest,
  getAllTransactions,
} from 'src/redux/services/billingServices';
import PatientInfo from './PatientInfo';
import PatientLedger from './PatientLedger';
import PaymentPlanDetails from './PaymentPlanDetails';
import PaymentPlanModal from './PaymentPlanModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchUserInformationByIdV1 } from 'src/api/user';
import { UserData } from 'src/latest/src/constants/UserData';
import ConfirmationModal from 'src/latest/src/components/ConfirmationModal/ConfirmationModal';
import SecondaryTab from 'src/latest/src/components/SecondaryTab/SecondaryTab';
import StripePayment from 'src/containers/QuestionnaireParent/QuestionOptionsLibrary/components/StripePayment/StripePayment';
import {
  BillingDashboardTabName,
  dataKey,
  directoryPatientTransactionTableHeaderData,
  DirectoryRow,
  filterOptionsText,
  SortingKey,
  TableData,
  Transaction,
  TransactionResponse,
} from '../billing-admin-home/BIllingAdminConstant';
import PatientTransaction from './PatientTransaction';
import { Logger } from 'src/latest/src/services';
import { debounce } from 'lodash';
import { searchSuperAdminUser } from 'src/redux/services/superAdminServices';
import ChargePatientModal from 'src/latest/src/components/ChargePatientModal/ChargePatientModal';
import FilterIcon from '../../../../../assets/icons/icon-filter.svg';
import { Typography } from 'src/latest/src/components';
import { Radio, Space } from 'antd';

interface PatientDetailsPageProps {
  isDirectoryPlatform: boolean;
  directoryPatientData: DirectoryRow | null;
}

const PatientDetailsPage: React.FC<PatientDetailsPageProps> = ({
  isDirectoryPlatform,
  directoryPatientData,
}) => {
  const [ledgerEntries, setLedgerEntries] = useState<LedgerEntry[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [paymentPlans, setPaymentPlans] = useState<PaymentPlan | null>(null);
  const navigate = useNavigate();
  const { clientId } = useParams();
  const [loading, setLoading] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);
  const [isPauseModalOpen, setIsPauseModalOpen] = useState<boolean>(false);
  const [isEditConfirmModalOpen, setIsEditConfirmModalOpen] =
    useState<boolean>(false);
  const [planToPause, setPlanToPause] = useState<string | null>(null);
  const [planToDelete, setPlanToDelete] = useState<string | null>(null);
  const [planToSave, setPlanToSave] = useState<any | null>(null);
  const [modalMode, setModalMode] = useState<
    PatientDetailsModalMode.CREATE | PatientDetailsModalMode.EDIT
  >(PatientDetailsModalMode.CREATE);
  const [editingPlan, setEditingPlan] = useState<PaymentPlan | undefined>(
    undefined,
  );
  const [clientBalance, setClientBalance] = useState<ClientBalance | null>(
    null,
  );
  const [userInfo, setUserInfo] = useState<UserData | null>(null);
  const [isUserInfoFetching, setIsUserInfoFetching] = useState(false);
  const [activeTab, setActiveTab] = useState<PatientDetailsTabName>(
    PatientDetailsTabName.HOME,
  );

  const [transactionData, setTransactionData] =
    useState<TransactionResponse | null>(null);
  const [transactionCurrentPage, setTransactionCurrentPage] =
    useState<number>(1);
  const [transactionTotalPages, setTransactionTotalPages] = useState(0);
  const [transactionLimit] = useState<number>(10);
  const [transactionSortConfig, setTransactionSortConfig] = useState<{
    key: string;
    direction: SortingKey.ASC | SortingKey.DESC;
  } | null>(null);
  const [transactionHeaderData, setTransactionHeaderData] = useState<
    TableData[]
  >([]);
  const [currentTransaction, setCurrentTransaction] =
    useState<Transaction | null>(null);
  const [showRefundModal, setShowRefundModal] = useState<boolean>(false);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [, setError] = useState<string | null>(null);
  const [searchIds, setSearchIds] = useState<string[] | null>(null);
  const latestRequestId = useRef(0);
  const [balanceFilter, setBalanceFilter] =
    useState<BalanceFilterType>('BY_ALL');
  const children = <img className="filter-icon" src={FilterIcon} alt="Filter Icon" />;

  useEffect(() => {
    if (clientId) {
      fetchInitialDetails(clientId);
    }
  }, [clientId, currentPage]);

  useEffect(() => {
    if (activeTab === PatientDetailsTabName.HOME) {
      getPlanDetails();
    }

    if (activeTab === PatientDetailsTabName.PATIENT_LEDGER) {
      if (isDirectoryPlatform) {
        const requestId = Date.now();

        latestRequestId.current = requestId;

        const currentPage = getCurrentPage();
        const currentSortConfig = getCurrentSortConfig();

        if (searchIds && searchIds.length > 0) {
          fetchTransactionData(
            searchIds,
            requestId,
            currentPage,
            currentSortConfig,
          );
        } else if (searchIds === null) {
          fetchTransactionData([], requestId, currentPage, currentSortConfig);
        }
      }
    }
  }, [activeTab, transactionCurrentPage, transactionSortConfig, searchIds]);

  useEffect(() => {
    if (isDirectoryPlatform) {
      setTransactionHeaderData(directoryPatientTransactionTableHeaderData);
    }
  }, [isDirectoryPlatform]);

  useEffect(() => {
    if (balanceFilter && clientId && !isDirectoryPlatform) {
      fetchLedger(currentPage);
    }
  }, [balanceFilter]);

  const fetchInitialDetails = async (clientId: string) => {
    setLoading(true);
    setIsUserInfoFetching(true);

    try {
      if (isDirectoryPlatform && directoryPatientData) {
        await fetchUserInfo();
      } else {
        await fetchBalance(clientId);
        await fetchLedger(currentPage);
        await fetchUserInfo();
      }
    } catch (error) {
      Logger.error('Error fetching data', error);
    } finally {
      setLoading(false);
      setIsUserInfoFetching(false);
    }
  };

  const fetchTransactionData = async (
    clientIds: string[] = [],
    requestId: number,
    currentPage: number,
    sortConfig: {
      key: string;
      direction: SortingKey.ASC | SortingKey.DESC;
    } | null,
  ) => {
    try {
      setLoading(true);

      const validTransactionFields: string[] = [
        dataKey?.TRANSACTION_PATIENT_NAME,
        dataKey?.TRANSACTION_AMOUNT,
      ];

      const sortingDirectives = [];

      if (sortConfig?.key && validTransactionFields.includes(sortConfig.key)) {
        sortingDirectives.push({
          field_name: sortConfig.key,
          sort_type: sortConfig.direction,
        });
      }

      const payload = {
        providerIds: [directoryPatientData?.provider_id || ''],
        clientIds,
        transactionTypes: [],
        statuses: [],
        dateRange: {},
        sorting: {
          directives: sortingDirectives,
        },
        pagination: {
          page: currentPage - 1,
          limit: transactionLimit,
        },
      };

      const response = await getAllTransactions(payload);

      if (requestId === latestRequestId.current) {
        setTransactionData(response);
        setTransactionTotalPages(
          Math.ceil(response?.pagination?.total / transactionLimit),
        );
        setTotalCount(response?.pagination?.total || 0);
      }
    } catch (error) {
      if (requestId === latestRequestId.current) {
        Logger.error('Failed to fetch transaction data:', error);
      }
    } finally {
      if (requestId === latestRequestId.current) {
        setLoading(false);
      }
    }
  };

  const fetchLedger = async (page: number) => {
    try {
      const ledgerData =
        clientId &&
        (await getTransactionLedger(clientId, page, 20, balanceFilter));

      const newLedgerEntries: LedgerEntry[] = ledgerData.charges.map(
        (charge: Charge) => ({
          cptCode: charge.code,
          serviceDate: charge.service_date,
          provider: charge.provider_name,
          chargeAmt: charge.charge_amount || 0,
          appliedAmt: charge.applied_amount || 0,
          patientBal: charge.patient_balance || 0,
          insuranceBal: charge.insurance_balance || 0,
          totalBal: charge.total_balance || 0,
          isExpanded: false,
          details: charge.payments.map((payment: Payment) => ({
            type: payment.code,
            serviceDate: payment.service_date,
            appliedAmt: payment.amount || 0,
          })),
        }),
      );

      setLedgerEntries(newLedgerEntries);
      setClientBalance(ledgerData.client_balance);
      setTotalPages(ledgerData.total_pages || 0);
    } catch (error) {
      Logger.error('Error fetching transaction ledger:', error);
    }
  };

  const fetchUserInfo = async () => {
    try {
      const userInfoData =
        clientId &&
        (await fetchUserInformationByIdV1(clientId, 'PROFILE_DISPLAY', 'medium'));

      setUserInfo(userInfoData);
    } catch (error) {
      Logger.error('Error fetching user info:', error);
    }
  };

  const getPlanDetails = async () => {
    try {
      if (clientId) {
        const res = await getPaymentPlansLatest(clientId);

        setPaymentPlans(res);
      }
    } catch (err) {
      Logger.error(err);
    }
  };

  const handleTransactionSort = (key: string) => {
    let direction: SortingKey.ASC | SortingKey.DESC = SortingKey.ASC;

    if (transactionSortConfig && transactionSortConfig.key === key) {
      direction =
        transactionSortConfig.direction === SortingKey.ASC
          ? SortingKey.DESC
          : SortingKey.ASC;
    }

    setTransactionSortConfig({ key, direction });

    setTransactionHeaderData(prev =>
      prev.map(header =>
        header.key === key && header.sortable
          ? { ...header, orderBy: direction.toLowerCase() }
          : { ...header, orderBy: undefined },
      ),
    );
  };

  const getCurrentPage = () => {
    return transactionCurrentPage;
  };

  const getCurrentSortConfig = () => {
    return transactionSortConfig;
  };

  const handlePatientSearch = async (searchText: string) => {
    setLoading(true);
    setTransactionCurrentPage(1);

    try {
      const response = await searchSuperAdminUser(
        encodeURIComponent(searchText.trim()),
      );

      if (!response?.users?.length) {
        setError('No user data found');
        setSearchIds([]);
      } else {
        setError('');
        const ids = response.users.map((user: any) => user.id);

        setSearchIds(ids);
      }
    } catch (error: any) {
      setError(
        error?.response?.data?.message ||
          error?.message ||
          'Failed to fetch users data',
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSearchInputChange = (value: string) => {
    if (!value || value.length < 3) {
      debouncedSearch.cancel();
      setSearchIds(null);
      setTransactionCurrentPage(1);

      if (value === '') {
        const requestId = Date.now();

        latestRequestId.current = requestId;

        const currentPage = getCurrentPage();
        const currentSortConfig = getCurrentSortConfig();

        fetchTransactionData([], requestId, currentPage, currentSortConfig);
      }
    } else {
      debouncedSearch(value);
    }
  };

  const debouncedSearch = useMemo(
    () => debounce((value: string) => handlePatientSearch(value), 500),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleRefundClick = (transaction: Transaction) => {
    setCurrentTransaction(transaction);
    setShowRefundModal(true);
  };

  const handleTransactionPageChange = (page: number) => {
    setTransactionCurrentPage(page + 1);
  };

  const handlePause = (planId: string) => {
    setPlanToPause(planId);
    setIsPauseModalOpen(true);
  };

  const confirmPause = async () => {
    if (planToPause) {
      setLoading(true);

      try {
        await pausePaymentPlan(planToPause);
        await getPlanDetails();
      } catch (error) {
        Logger.error('Error pausing payment plan:', error);
      } finally {
        setLoading(false);
        setIsPauseModalOpen(false);
        setPlanToPause(null);
      }
    }
  };

  const handleResume = async (planId: string) => {
    setLoading(true);

    try {
      await resumePaymentPlan(planId);
      await getPlanDetails();
    } catch (error) {
      Logger.error('Error resuming payment plan:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (planId: string) => {
    setPlanToDelete(planId);
    setIsConfirmModalOpen(true);
  };

  const confirmDelete = async (reason: string | undefined) => {
    if (planToDelete) {
      setLoading(true);

      try {
        await deletePaymentPlan(planToDelete, reason || 'Cancelled by user');
        await getPlanDetails();
      } catch (error) {
        Logger.error('Error deleting payment plan:', error);
      } finally {
        setLoading(false);
        setIsConfirmModalOpen(false);
        setPlanToDelete(null);
      }
    }
  };

  const handleOpenCreateModal = () => {
    setModalMode(PatientDetailsModalMode.CREATE);
    setEditingPlan(undefined);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (plan: PaymentPlan) => {
    setModalMode(PatientDetailsModalMode.EDIT);
    setEditingPlan(plan);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleSavePlan = (planData: any) => {
    if (modalMode === PatientDetailsModalMode.EDIT) {
      setIsModalOpen(false);
      setPlanToSave(planData);
      setIsEditConfirmModalOpen(true);
    } else {
      confirmSave(planData);
    }
  };

  const confirmSave = async (planData: any) => {
    setLoading(true);

    try {
      if (modalMode === PatientDetailsModalMode.CREATE && clientId) {
        await createPaymentPlan({
          ...planData,
          clientId,
          maxFailures: 10,
          autoPauseOnFailure: true,
        });
      } else if (modalMode === PatientDetailsModalMode.EDIT && editingPlan) {
        await updatePaymentPlan(editingPlan.id, {
          ...(planToSave || planData),
          maxFailures: 10,
          autoPauseOnFailure: true,
        });
      }

      await getPlanDetails();
      handleCloseModal();
    } catch (error) {
      Logger.error('Error saving payment plan:', error);
    } finally {
      setLoading(false);
      setIsEditConfirmModalOpen(false);
      setPlanToSave(null);
    }
  };

  const toggleRowExpansion = (index: number) => {
    const updatedEntries = [...ledgerEntries];

    updatedEntries[index].isExpanded = !updatedEntries[index].isExpanded;
    setLedgerEntries(updatedEntries);
  };

  const handleBackNavigation = () => navigate('..');

  const afterChargeSucess = () => {
    const requestId = Date.now();

    latestRequestId.current = requestId;

    const currentPage = getCurrentPage();
    const currentSortConfig = getCurrentSortConfig();

    if (searchIds && searchIds.length > 0) {
      fetchTransactionData(
        searchIds,
        requestId,
        currentPage,
        currentSortConfig,
      );
    } else {
      fetchTransactionData([], requestId, currentPage, currentSortConfig);
    }
  };

  const content = () => (
    <div className="billing-admin-dashboard-popover-inner-content">
      <Typography className="mb-2" weight="semi-bold" variant="size-14">
        {filterOptionsText.filter_by_show}
      </Typography>
      <div className="vertical-alignment">
        <Space direction="vertical">
          <Radio
            checked={balanceFilter === 'BY_ALL'}
            onChange={() => setBalanceFilter('BY_ALL')}
          >
            All Items
          </Radio>
          <Radio
            checked={
              balanceFilter === 'BY_INSURANCE_BAL' ||
              balanceFilter === 'BY_PATIENT_BAL'
            }
            onChange={() => setBalanceFilter('BY_INSURANCE_BAL')}
          >
            Open Items
          </Radio>
          <div className="client-onboarding-progress">
            <Space direction="vertical">
              <Radio
                checked={balanceFilter === 'BY_INSURANCE_BAL'}
                onChange={() => setBalanceFilter('BY_INSURANCE_BAL')}
              >
                Insurance
              </Radio>
              <Radio
                checked={balanceFilter === 'BY_PATIENT_BAL'}
                onChange={() => setBalanceFilter('BY_PATIENT_BAL')}
              >
                Patient
              </Radio>
            </Space>
          </div>
        </Space>
      </div>
    </div>
  );

  return (
    <div className="patient-details-page">
      {loading && (
        <div className="loader-overlay">
          <div className="loader" />
        </div>
      )}
      <div onClick={handleBackNavigation} className="back-link cursor-pointer">
        <FontAwesomeIcon icon={faArrowLeft} />
        {isDirectoryPlatform ? 'Provider' : 'Patient'} Details
      </div>

      <SecondaryTab
        activeTab={activeTab}
        onTabChange={setActiveTab}
        tabs={[
          { label: 'Home', value: PatientDetailsTabName.HOME },
          {
            label: isDirectoryPlatform ? 'Transactions' : 'Patient Ledger',
            value: PatientDetailsTabName.PATIENT_LEDGER,
          },
          {
            label: 'Manage Cards',
            value: PatientDetailsTabName.MANAGE_CARDS,
          },
        ]}
      />

      {activeTab === PatientDetailsTabName.HOME && (
        <div className="details-header">
          <PatientInfo
            patientData={{
              name: isDirectoryPlatform
                ? directoryPatientData?.provider_name || 'N/A'
                : clientBalance?.client_name || 'N/A',
              phone: userInfo?.telephone_number || 'N/A',
              patientBalance: isDirectoryPlatform
                ? directoryPatientData?.provider_balance || 0
                : parseFloat(clientBalance?.patient_balance_amount || '0'),
              insuranceBalance: parseFloat(
                clientBalance?.insurance_balance_amount || '0',
              ),
              totalBalance: parseFloat(
                clientBalance?.total_balance_amount || '0',
              ),
              photoUrl: userInfo?.profile_pic,
            }}
            isLoading={isUserInfoFetching}
            isDirectoryPlatform={isDirectoryPlatform}
          />
          <PaymentPlanDetails
            paymentPlan={paymentPlans}
            clientID={clientId}
            onPause={handlePause}
            onResume={handleResume}
            onDelete={handleDelete}
            onCreate={handleOpenCreateModal}
            onEdit={handleOpenEditModal}
            isLoading={loading}
          />
        </div>
      )}

      {activeTab === PatientDetailsTabName.PATIENT_LEDGER &&
        (isDirectoryPlatform ? (
          <PatientTransaction
            loading={loading}
            isDirectoryPlatform={isDirectoryPlatform}
            activeTab={BillingDashboardTabName.TRANSACTIONS}
            handleTransactionSort={handleTransactionSort}
            transactionData={transactionData}
            transactionTotalPages={transactionTotalPages}
            transactionCurrentPage={transactionCurrentPage}
            handleTransactionPageChange={handleTransactionPageChange}
            transactionHeaderData={transactionHeaderData}
            totalCount={totalCount}
            handleSearchInputChange={handleSearchInputChange}
            handleRefundClick={handleRefundClick}
          />
        ) : (
          <PatientLedger
            ledgerEntries={ledgerEntries}
            toggleRowExpansion={toggleRowExpansion}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalPages={totalPages}
            children={children}
            content={content}
          />
        ))}

      {activeTab === PatientDetailsTabName.MANAGE_CARDS && (
        <StripePayment fromOnboarding={false} manageCardClientId={clientId} />
      )}

      <PaymentPlanModal
        show={isModalOpen}
        mode={modalMode}
        plan={editingPlan}
        onClose={handleCloseModal}
        onSave={handleSavePlan}
        userInfo={userInfo}
        clientBalance={clientBalance}
        clientId={clientId}
        isDirectoryPlatform={isDirectoryPlatform}
        directoryPatientData={directoryPatientData}
      />

      <ConfirmationModal
        show={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={confirmDelete}
        title="Are you sure to cancel this payment plan?"
        confirmText="Cancel plan"
        cancelText="Not now"
        isDangerPrimary={true}
        isRequiredCloseIcon={false}
        isReasonRequired={true}
        isLoading={loading}
      />

      <ConfirmationModal
        show={isPauseModalOpen}
        onClose={() => setIsPauseModalOpen(false)}
        onConfirm={confirmPause}
        title="Are you sure to pause this payment plan?"
        confirmText="Pause plan"
        cancelText="Not now"
        isDangerPrimary={true}
        isRequiredCloseIcon={false}
        isLoading={loading}
      />

      <ConfirmationModal
        show={isEditConfirmModalOpen}
        onClose={() => setIsEditConfirmModalOpen(false)}
        onConfirm={() => confirmSave(planToSave)}
        title="Are you sure to update this payment plan?"
        confirmText="Update plan"
        cancelText="Not now"
        isDangerPrimary={true}
        isRequiredCloseIcon={false}
        isLoading={loading}
      />

      {isDirectoryPlatform && showRefundModal && currentTransaction && (
        <ChargePatientModal
          patientData={null}
          directoryPatientData={null}
          afterChargeSucess={afterChargeSucess}
          showModal={showRefundModal}
          setShowModal={setShowRefundModal}
          transactionData={currentTransaction}
          paymentMethods={null}
          isDirectoryPlatform={isDirectoryPlatform}
        />
      )}
    </div>
  );
};

export default PatientDetailsPage;
